import { useState } from 'react';
import { Modal } from '../../components/common';
import { IconSave } from '../../components/icons';
import { Switch } from './shell';

const SESSIONS = 'sessions';
const VALUE = 'value';

const numberIds = (value) => (Array.isArray(value) ? value.map(Number) : []);

export default function PackageDialog({
  packageRow, branches, services, onSubmit, onClose,
}) {
  const item = packageRow?.items?.[0] || {};
  const initialType = packageRow?.package_type
    || (packageRow ? (item.creditAmount != null ? VALUE : SESSIONS) : VALUE);
  const initialBranches = numberIds(packageRow?.branch_ids);
  const savedServices = numberIds(packageRow?.service_ids);
  // Legacy packages pointed straight at one duration row. Present that as the
  // matching service scope instead of making an old Thai-massage package look
  // like it applies to every service when first opened in the new dialog.
  const initialServices = savedServices.length
    ? savedServices
    : (item.serviceId ? [Number(item.serviceId)] : []);
  const [v, setV] = useState(() => ({
    name: packageRow?.name_th || packageRow?.name_en || '',
    packageType: initialType,
    branchScope: initialBranches.length ? 'some' : 'all',
    branchIds: initialBranches,
    serviceScope: initialServices.length ? 'some' : 'all',
    serviceIds: initialServices,
    price: packageRow?.price ?? '',
    creditAmount: item.creditAmount ?? '',
    quantity: item.quantity ?? '',
    validityDays: packageRow
      ? (packageRow.validity_days ?? 9999)
      : '',
    isActive: packageRow ? packageRow.is_active !== false : true,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setV((current) => ({ ...current, [key]: value }));
  const toggle = (key, id) => setV((current) => ({
    ...current,
    [key]: current[key].includes(id)
      ? current[key].filter((value) => value !== id)
      : [...current[key], id],
  }));

  const submit = async () => {
    if (!v.name.trim()) return setError('กรุณากรอกชื่อแพ็กเกจ');
    if (v.price === '' || Number(v.price) < 0) return setError('กรุณากรอกราคาขาย');
    if (v.packageType === VALUE && !(Number(v.creditAmount) > 0)) {
      return setError('กรุณากรอกมูลค่าแทนเงินสดที่ได้รับ');
    }
    if (v.packageType === SESSIONS && !(Number(v.quantity) > 0)) {
      return setError('กรุณากรอกจำนวนครั้ง');
    }
    if (!(Number(v.validityDays) > 0)) {
      return setError('กรุณากรอกระยะเวลาใช้งาน');
    }
    if (v.branchScope === 'some' && !v.branchIds.length) {
      return setError('กรุณาเลือกอย่างน้อยหนึ่งสาขา');
    }
    if (v.serviceScope === 'some' && !v.serviceIds.length) {
      return setError('กรุณาเลือกอย่างน้อยหนึ่งบริการ');
    }

    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        code: packageRow?.code,
        nameEn: v.name.trim(),
        nameTh: null,
        packageType: v.packageType,
        branchIds: v.branchScope === 'all' ? [] : v.branchIds,
        serviceIds: v.serviceScope === 'all' ? [] : v.serviceIds,
        price: Number(v.price),
        validityDays: Number(v.validityDays),
        isActive: v.isActive,
        items: v.packageType === VALUE
          ? [{ creditAmount: Number(v.creditAmount) }]
          : [{ quantity: Number(v.quantity) }],
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const radio = (name, value, label) => (
    <label className={`radio${v[name] === value ? ' on' : ''}`}>
      <input
        type="radio"
        name={name}
        checked={v[name] === value}
        onChange={() => set(name, value)}
      />
      {label}
    </label>
  );

  return (
    <Modal
      title={packageRow ? 'แก้ไขแพ็กเกจ' : 'สร้างแพ็กเกจ'}
      className="package-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            <IconSave size={16} /> {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}

      <div className="field">
        <label>ชื่อแพ็กเกจ <span className="required">*</span></label>
        <input
          value={v.name}
          onChange={(event) => set('name', event.target.value)}
          placeholder="ชื่อแพ็กเกจ"
        />
      </div>

      <div className="field">
        <label>ประเภทแพ็กเกจ <span className="required">*</span></label>
        <select
          value={v.packageType}
          onChange={(event) => set('packageType', event.target.value)}
        >
          <option value={VALUE}>แทนเงินสด</option>
          <option value={SESSIONS}>จำนวนครั้ง</option>
        </select>
      </div>

      <div className="package-scope">
        <strong>สาขา <span className="required">*</span></strong>
        <div className="package-radio-row">
          {radio('branchScope', 'all', 'ทุกสาขา')}
          {radio('branchScope', 'some', 'บางสาขา')}
        </div>
        {v.branchScope === 'some' && (
          <div className="package-choice-grid">
            {branches.map((branch) => (
              <label key={branch.id} className="package-check">
                <input
                  type="checkbox"
                  checked={v.branchIds.includes(Number(branch.id))}
                  onChange={() => toggle('branchIds', Number(branch.id))}
                />
                {branch.nameTh || branch.name_th || branch.code}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="package-scope">
        <strong>บริการ <span className="required">*</span></strong>
        <div className="package-radio-row">
          {radio('serviceScope', 'all', 'ทุกบริการ')}
          {radio('serviceScope', 'some', 'บางบริการ')}
        </div>
        {v.serviceScope === 'some' && (
          <div className="package-choice-grid package-service-grid">
            {services.map((service) => (
              <label key={service.id} className="package-check">
                <input
                  type="checkbox"
                  checked={v.serviceIds.includes(Number(service.id))}
                  onChange={() => toggle('serviceIds', Number(service.id))}
                />
                {service.name_th || service.name_en}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="field">
        <label>ราคาขาย <span className="required">*</span></label>
        <input
          type="number"
          min="0"
          value={v.price}
          onChange={(event) => set('price', event.target.value)}
        />
      </div>

      {v.packageType === VALUE ? (
        <div className="field">
          <label>มูลค่าแทนเงินสดที่ได้รับ <span className="required">*</span></label>
          <input
            type="number"
            min="0"
            value={v.creditAmount}
            onChange={(event) => set('creditAmount', event.target.value)}
          />
        </div>
      ) : (
        <div className="field">
          <label>จำนวนครั้ง <span className="required">*</span></label>
          <input
            type="number"
            min="1"
            value={v.quantity}
            onChange={(event) => set('quantity', event.target.value)}
          />
          <span className="package-hint">นับรวมครั้งที่แถม — ซื้อ 10 ฟรี 1 คือ 11</span>
        </div>
      )}

      <div className="field">
        <label>ระยะเวลาใช้งาน (วัน) เริ่มนับหลังวันซื้อ <span className="required">*</span></label>
        <input
          type="number"
          min="0"
          value={v.validityDays}
          onChange={(event) => set('validityDays', event.target.value)}
          placeholder="0"
        />
        <span className="package-hint">ใส่ 9999 สำหรับแพ็กเกจที่ไม่มีวันหมดอายุ</span>
      </div>

      <Switch
        label="สถานะ"
        hint="ไม่ใช้งาน / ใช้งาน"
        on={v.isActive}
        onChange={(value) => set('isActive', value)}
      />
    </Modal>
  );
}
